if (!customElements.get('product-sticky-atc')) {
  customElements.define(
    'product-sticky-atc',
    class ProductStickyAtc extends HTMLElement {
      sectionId;
      productInfo;
      mainSubmit;
      mainQty;
      mirrorQty;
      stickySubmit;
      qtyIntersecting = true;
      submitIntersecting = true;
      qtyObserver;
      submitObserver;
      submitStateObserver;
      mq;
      onMqChange;
      onProductInfoInput;
      onProductInfoChange;
      onMirrorInput;
      onMirrorChange;
      onStickyClick;
      variantUnsub;

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productInfo = this.closest('product-info');
        if (!this.productInfo || !this.sectionId) return;

        this.mainSubmit = this.productInfo.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        if (!this.mainSubmit) return;

        this.mainQty = this.productInfo.querySelector(`#Quantity-${this.sectionId}`);
        this.mirrorQty = this.querySelector(`#StickyQuantity-${this.sectionId}`);
        this.stickySubmit = this.querySelector('[data-sticky-atc-submit]');

        this.mq = window.matchMedia('(max-width: 749px)');
        this.onMqChange = () => this.refreshMobileMode();
        this.mq.addEventListener('change', this.onMqChange);

        this.onProductInfoInput = (event) => {
          if (event.target.id === `Quantity-${this.sectionId}`) this.syncMainToMirror();
        };
        this.onProductInfoChange = (event) => {
          if (event.target.id === `Quantity-${this.sectionId}`) this.syncMainToMirror();
        };
        this.productInfo.addEventListener('input', this.onProductInfoInput);
        this.productInfo.addEventListener('change', this.onProductInfoChange);

        if (this.mirrorQty && this.mainQty) {
          this.onMirrorInput = () => this.syncMirrorToMain();
          this.onMirrorChange = () => this.syncMirrorToMain();
          this.mirrorQty.addEventListener('input', this.onMirrorInput);
          this.mirrorQty.addEventListener('change', this.onMirrorChange);
        }

        this.onStickyClick = (event) => {
          event.preventDefault();
          if (!this.mainSubmit || this.mainSubmit.getAttribute('aria-disabled') === 'true') return;
          if (this.mainSubmit.disabled) return;
          this.mainSubmit.click();
        };
        this.stickySubmit?.addEventListener('click', this.onStickyClick);

        this.variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== this.sectionId) return;
          requestAnimationFrame(() => {
            this.mainSubmit = this.productInfo.querySelector(`#ProductSubmitButton-${this.sectionId}`);
            this.mainQty = this.productInfo.querySelector(`#Quantity-${this.sectionId}`);
            this.syncMainToMirror();
            this.syncSubmitMirrorFromMain();
            this.bindSubmitStateMirror();
            this.refreshObserversTargets();
            this.updateBarVisibility();
          });
        });

        this.qtyObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;
            this.qtyIntersecting = entry.isIntersecting;
            this.updateBarVisibility();
          },
          { threshold: 0 }
        );

        this.submitObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;
            this.submitIntersecting = entry.isIntersecting;
            this.updateBarVisibility();
          },
          { threshold: 0 }
        );

        this.syncMainToMirror();
        this.syncSubmitMirrorFromMain();
        this.bindSubmitStateMirror();
        this.refreshMobileMode();
      }

      disconnectedCallback() {
        this.submitStateObserver?.disconnect();
        this.mq?.removeEventListener('change', this.onMqChange);
        this.productInfo?.removeEventListener('input', this.onProductInfoInput);
        this.productInfo?.removeEventListener('change', this.onProductInfoChange);
        this.mirrorQty?.removeEventListener('input', this.onMirrorInput);
        this.mirrorQty?.removeEventListener('change', this.onMirrorChange);
        this.stickySubmit?.removeEventListener('click', this.onStickyClick);
        this.variantUnsub?.();
        this.qtyObserver?.disconnect();
        this.submitObserver?.disconnect();
        document.body.classList.remove('product-sticky-atc--pad');
      }

      bindSubmitStateMirror() {
        this.submitStateObserver?.disconnect();
        if (!this.mainSubmit) return;
        this.submitStateObserver = new MutationObserver(() => this.syncSubmitMirrorFromMain());
        this.submitStateObserver.observe(this.mainSubmit, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });
      }

      refreshObserversTargets() {
        const qtyForm = this.productInfo.querySelector(`#Quantity-Form-${this.sectionId}`);
        this.qtyObserver?.disconnect();
        if (qtyForm) {
          this.qtyObserver.observe(qtyForm);
        }
        this.submitObserver?.disconnect();
        if (this.mainSubmit) {
          this.submitObserver.observe(this.mainSubmit);
        }
      }

      refreshMobileMode() {
        if (this.mq.matches) {
          this.refreshObserversTargets();
          this.updateBarVisibility();
        } else {
          this.qtyObserver?.disconnect();
          this.submitObserver?.disconnect();
          this.setBarShown(false);
        }
      }

      updateBarVisibility() {
        if (!this.mq.matches || !this.mainSubmit) {
          this.setBarShown(false);
          return;
        }

        const hasQtyZone = !!this.productInfo.querySelector(`#Quantity-Form-${this.sectionId}`);
        const show = hasQtyZone
          ? !this.qtyIntersecting && !this.submitIntersecting
          : !this.submitIntersecting;

        this.setBarShown(show);
      }

      setBarShown(show) {
        if (!this.mq.matches) {
          this.setAttribute('hidden', '');
          this.setAttribute('inert', '');
          document.body.classList.remove('product-sticky-atc--pad');
          return;
        }

        if (show) {
          this.removeAttribute('hidden');
          this.removeAttribute('inert');
          document.body.classList.add('product-sticky-atc--pad');
          this.syncMainToMirror();
          this.syncSubmitMirrorFromMain();
        } else {
          this.setAttribute('hidden', '');
          this.setAttribute('inert', '');
          document.body.classList.remove('product-sticky-atc--pad');
        }
      }

      syncMirrorToMain() {
        if (!this.mirrorQty || !this.mainQty) return;
        if (this.mirrorQty.value === this.mainQty.value) return;
        this.mainQty.value = this.mirrorQty.value;
        this.mainQty.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncMainToMirror() {
        if (!this.mirrorQty || !this.mainQty) return;
        const attrs = ['min', 'max', 'step', 'data-min', 'data-max', 'data-cart-quantity'];
        for (const name of attrs) {
          if (this.mainQty.hasAttribute(name)) {
            this.mirrorQty.setAttribute(name, this.mainQty.getAttribute(name));
          } else {
            this.mirrorQty.removeAttribute(name);
          }
        }
        this.mirrorQty.value = this.mainQty.value;
        this.mirrorQty.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncSubmitMirrorFromMain() {
        const main = this.productInfo?.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        const sticky = this.stickySubmit;
        if (!main || !sticky) return;

        sticky.disabled = main.disabled;
        if (main.getAttribute('aria-disabled') === 'true') {
          sticky.setAttribute('aria-disabled', 'true');
        } else {
          sticky.removeAttribute('aria-disabled');
        }

        const mainSoldOut = main.querySelector('.sold-out-message:not(.hidden)');
        const mainLabel =
          mainSoldOut ||
          Array.from(main.querySelectorAll('span')).find(
            (el) => !el.classList.contains('hidden') && !el.closest('.loading__spinner')
          );
        const stickyLabel = sticky.querySelector('[data-sticky-atc-submit-label]');
        if (mainLabel && stickyLabel) {
          stickyLabel.textContent = mainLabel.textContent.trim();
        }

        sticky.classList.remove('button--primary', 'button--secondary');
        if (main.classList.contains('button--secondary')) {
          sticky.classList.add('button--secondary');
        } else {
          sticky.classList.add('button--primary');
        }

        sticky.classList.toggle('loading', main.classList.contains('loading'));
        const mainSpinner = main.querySelector('.loading__spinner');
        const stickySpinner = sticky.querySelector('.loading__spinner');
        if (mainSpinner && stickySpinner) {
          stickySpinner.classList.toggle('hidden', mainSpinner.classList.contains('hidden'));
        }
      }
    }
  );
}
